import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'
import * as shared from '../../../shared'

const SPACE_DIMENSION_ID = ents.core.dms.models.Dimension.Id.from(
  ents.extra.quiver.resources.data.dimensions.space.RESOURCE.payload,
)

const BUILDING_ENTITY_ID =
  ents.extra.quiver.resources.data.dimensions.space.entities.building.MODEL_ID

/** Modal for assigning existing space records to a cluster. */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.checkbox.Component,
    ds.layout.widget.Component,
    ds.loader.Component,
    ds.overlay.centered.Component,
    ds.tag.Component,
    shared.dimensionFilter.Component,
  ],
  selector: 'atn-space-assign-records-to-cluster-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.full.clusterAssignableRecords.Collection} */
  public readonly assignableEc = ngCore.inject(
    state.space.full.clusterAssignableRecords.Collection,
  )

  /** @see {@link state.space.full.clusterAssignedRecords.Collection} */
  public readonly assignedEc = ngCore.inject(
    state.space.full.clusterAssignedRecords.Collection,
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link state.space.full.clusterAssignableRecords.plugins.spaceDimensionFilter.Plugin} */
  public readonly dimensionFilterPlugin = this.assignableEc.plugins
    .lookup(
      state.space.full.clusterAssignableRecords.plugins.spaceDimensionFilter
        .Plugin,
    )
    .get()

  /** Records available for selection (not yet assigned) */
  public readonly recordsSg = ngCore.computed(
    (
      allRecords = this.assignableEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
      assignedRecords = this.assignedEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) => {
      const assignedIds = new Set(assignedRecords.map(record => record.id))
      return allRecords.filter(record => !assignedIds.has(record.id))
    },
  )

  /** Currently selected records */
  public readonly selectedRecordsSg = ngCore.computed(
    (
      selected = this.assignableEc.plugins.selectionPlugin.feature.selectors
        .getAllSelected({
          from: 'applied',
          target: this.assignedEc.associations?.selectionTarget,
        })
        .asSignal(),
    ) => selected.filter(TypeChecker.isNotNullish.raw),
  )

  /** Modal title — "Add <LeafEntityName>s to Cluster" (e.g. "Add Rooms to Cluster") */
  public readonly titleSg = ngCore.computed(
    (schema = this._schemaFeature.selectors.schema.asSignal()) => {
      // TODO: Handle pluralization properly
      const leafName = schema.tryGetLeafEntityOf(SPACE_DIMENSION_ID)?.name
      return leafName ? `Add ${leafName}s to Cluster` : 'Add Records to Cluster'
    },
  )

  /** Section title — leaf entity name with selected count when any are checked (e.g. "Room (3)") */
  public readonly recordsSectionTitleSg = ngCore.computed(
    (
      schema = this._schemaFeature.selectors.schema.asSignal(),
      selectedCount = this.selectedRecordsSg().length,
    ) => {
      const label =
        schema.tryGetLeafEntityOf(SPACE_DIMENSION_ID)?.name ?? 'Records'
      return selectedCount > 0 ? `${label} (${selectedCount})` : label
    },
  )

  /** Whether a given record is currently selected */
  public isRecordSelected(id: ents.extra.space.Record.Id): boolean {
    return this.selectedRecordsSg().some(r => r.id === id)
  }

  /**
   * Returns "BuildingName - RecordName" (e.g. "Harris Hall - 114").
   * Falls back to just the record name if the building entity is unknown.
   */
  public getRecordLabel(record: ents.extra.space.Record): string {
    const buildingName = record.ancestors[BUILDING_ENTITY_ID]?.name
    return buildingName ? `${buildingName} - ${record.name}` : record.name
  }

  /** Toggles a record checkbox */
  public recordCheckboxChangedHandler(
    id: ents.extra.space.Record.Id,
    selected: boolean,
  ): void {
    this.assignableEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from('AssignRecordsToCluster.Modal.RecordCheckbox.Toggled')
      .dispatch({
        key: this.assignedEc.associations?.selectionTarget,
        value: selected ? { add: id } : { remove: id },
      })
  }

  /** Removes a record from the selection */
  public removeSelectedRecord(id: ents.extra.space.Record.Id): void {
    this.assignableEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from('AssignRecordsToCluster.Modal.SelectedTag.Removed')
      .dispatch({
        key: this.assignedEc.associations?.selectionTarget,
        value: { remove: id },
      })
  }

  /** Saves the selected records */
  public saveClickHandler(): void {
    this.assignedEc.plugins.editingPlugin.feature.actions.submit
      .from('AssignRecordsToCluster.Modal.Save.Clicked')
      .dispatch({ create: true })
  }

  /** Cancels without saving */
  public cancelClickHandler(): void {
    this.assignedEc.plugins.editingPlugin.feature.actions.discard
      .from('AssignRecordsToCluster.Modal.Cancel.Clicked')
      .dispatch({ create: true })
  }
}
